import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Loader2, AlertTriangle, Shield, Layers, Clock, ExternalLink, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';
import { QueueConsumerConfig, ConsumerRole } from '../../types';
import Tooltip from '../common/Tooltip';

interface ConsumerRegistryPanelProps {
  queueId: number;
  projectId: string;
  singleConsumerMode: boolean;
}

/**
 * AC-462 + AC-470 + AC-475: Consumer Registry Panel (Read-Only)
 *
 * Displays workflows configured to consume from this queue.
 * Data is sourced from workflow step configurations (source of truth),
 * not runtime consumer registrations.
 *
 * This is a read-only view - to change consumer roles, edit the queue_read
 * step configuration in the workflow editor.
 */
const ConsumerRegistryPanel: React.FC<ConsumerRegistryPanelProps> = ({
  queueId,
  projectId,
  singleConsumerMode,
}) => {
  const [consumers, setConsumers] = useState<QueueConsumerConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AC-475: Fetch from step configs (source of truth) instead of runtime registrations
  const fetchConsumers = useCallback(async () => {
    try {
      const data = await apiService.getQueueConsumerConfigs(queueId);
      setConsumers(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch consumer configs:', err);
      setError('Failed to load consumers');
    }
  }, [queueId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchConsumers();
      setLoading(false);
    };
    loadData();
  }, [fetchConsumers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConsumers();
    setRefreshing(false);
  };

  const getRoleBadgeClass = (role: ConsumerRole) => {
    switch (role) {
      case 'PRIMARY':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'PARALLEL':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'BACKUP':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getRoleIcon = (role: ConsumerRole) => {
    switch (role) {
      case 'PRIMARY':
        return <Shield className="w-3 h-3" />;
      case 'PARALLEL':
        return <Layers className="w-3 h-3" />;
      case 'BACKUP':
        return <Clock className="w-3 h-3" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500 dark:text-gray-400">Loading consumers...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Users className="w-5 h-5 text-gray-500 dark:text-gray-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">Registered Consumers</h3>
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({consumers.length})</span>
        </div>
        <Tooltip content="Refresh consumer list">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </Tooltip>
      </div>

      {/* Info banner about configuration source */}
      <div className="mb-4 p-3 bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
        <p>
          Shows workflows with <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">queue_read</code> steps
          configured to consume from this queue. Roles reflect current step configuration.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {singleConsumerMode && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-start">
          <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>Single consumer mode is enabled. Only one PRIMARY or PARALLEL consumer is allowed.</span>
        </div>
      )}

      {consumers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Users className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">No consumers registered</p>
          <p className="text-xs mt-1">
            To add a consumer, create a workflow with a{' '}
            <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">queue_read</code> step that reads from this queue.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {consumers.map((consumer) => (
            <div
              key={consumer.orchestration_id}
              className="p-4 rounded-lg border bg-white dark:bg-cap-dark border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <Link
                  to={`/projects/${projectId}/workflows/${consumer.orchestration_id}`}
                  className="flex items-center text-primary-600 hover:text-primary-800 hover:underline font-medium"
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  {consumer.orchestration_name || `Orchestration #${consumer.orchestration_id}`}
                </Link>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(
                    consumer.consumer_role
                  )}`}
                >
                  {getRoleIcon(consumer.consumer_role)}
                  <span className="ml-1">{consumer.consumer_role}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer guidance */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          To change consumer roles, edit the{' '}
          <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">queue_read</code> step configuration in your workflow.
        </p>
      </div>
    </div>
  );
};

export default ConsumerRegistryPanel;
